// @boty/ai - Suggested questions generation
// Generates suggested questions for testing a bot

import { getOpenAIClient, AI_MODELS, handleOpenAIError } from './provider';
import type { AIUsageCallback } from './types';

/**
 * Options for generating suggested questions
 */
export interface SuggestedQuestionsOptions {
  /** Bot ID for context */
  botId: string;
  /** Number of questions to generate (default: 3) */
  count?: number;
  /** Questions the AI must NOT echo (for regenerate-one flows). Case/whitespace-insensitive. */
  avoidQuestions?: string[];
  /** Optional callback for reporting LLM usage data */
  onUsage?: AIUsageCallback;
}

/**
 * Result from question suggestion
 */
export interface SuggestedQuestionsResult {
  /** Generated questions */
  questions: string[];
  /** Topics extracted from content */
  topics: string[];
  /** ISO timestamp of generation */
  generatedAt: string;
}

/**
 * System prompt template for generating suggested questions.
 * `{count}` and `{avoid}` are filled in per call so the AI both produces the
 * requested number of questions and avoids echoing kept questions in the
 * regenerate-one flow.
 */
const SUGGESTED_QUESTIONS_PROMPT = `Analyze the following content and generate EXACTLY {count} suggested questions that a user might ask this chatbot.

CONTENT:
{content}
{avoid}
CRITICAL REQUIREMENTS:
1. All {count} questions MUST be directly answerable from the content above
2. DO NOT generate questions about information that isn't in the content
3. Questions should be natural and casual, not formal (e.g., "you open Sunday?" not "What are your hours of operation on Sunday?")
4. Match the language style of the content (if content is informal, questions should be informal)
5. Questions should cover different aspects/topics from the content
6. Each question should help users understand what the bot knows about
7. Each question must be unique — do not paraphrase the same idea

Also identify 3-5 main topics that this content covers (e.g., "schedule", "pricing", "location", "services").

Respond in JSON format:
{
  "questions": [{questionsExample}],
  "topics": ["topic1", "topic2", "topic3"]
}`;

/**
 * Raw response structure from AI
 */
interface AIQuestionsResponse {
  questions: string[];
  topics: string[];
}

/**
 * Generates suggested questions based on bot content
 * Questions are pre-validated to be answerable from the content
 *
 * @param content - Combined bot content
 * @param options - Generation options
 * @returns Suggested questions and topics
 */
export async function generateSuggestedQuestions(
  content: string,
  options: SuggestedQuestionsOptions
): Promise<SuggestedQuestionsResult> {
  const { count = 3, avoidQuestions = [], onUsage } = options;

  // Truncate content if too long (keep first 30K chars)
  const truncatedContent = content.length > 30000 ? content.substring(0, 30000) + '...' : content;

  const avoidBlock =
    avoidQuestions.length > 0
      ? `\nDO NOT generate any of these questions (the user has chosen to keep them — pick different angles):\n${avoidQuestions
          .map((q) => `- ${q}`)
          .join('\n')}\n`
      : '';
  const questionsExample = Array.from({ length: count }, (_, i) => `"question ${i + 1} here"`).join(', ');

  const client = getOpenAIClient();
  const prompt = SUGGESTED_QUESTIONS_PROMPT.replace('{content}', truncatedContent)
    .replace('{questionsExample}', questionsExample)
    .replace('{avoid}', avoidBlock)
    .replace(/\{count\}/g, String(count));

  try {
    const startTime = Date.now();
    const response = await client.chat.completions.create({
      model: AI_MODELS.GPT4O_MINI,
      messages: [
        {
          role: 'system',
          content:
            'You are a helpful assistant that generates suggested questions for chatbots. Always respond with valid JSON.',
        },
        { role: 'user', content: prompt },
      ],
      temperature: 0.7,
      max_tokens: 500,
      response_format: { type: 'json_object' },
    });

    if (onUsage) {
      try {
        onUsage({
          model: AI_MODELS.GPT4O_MINI,
          provider: 'openai',
          inputTokens: response.usage?.prompt_tokens ?? 0,
          outputTokens: response.usage?.completion_tokens ?? 0,
          latencyMs: Date.now() - startTime,
          functionName: 'generateSuggestedQuestions',
          inputMessages: [
            { role: 'system', content: 'Generate suggested questions for a chatbot.' },
            { role: 'user', content: prompt.slice(0, 1000) },
          ],
          ...(response.choices[0]?.message?.content && { outputText: response.choices[0].message.content.slice(0, 500) }),
        });
      } catch { /* never break the call */ }
    }

    const responseContent = response.choices[0]?.message?.content;
    if (!responseContent) {
      throw new Error('Empty response from AI');
    }

    const parsed: AIQuestionsResponse = JSON.parse(responseContent);

    // Validate response structure
    if (!Array.isArray(parsed.questions) || parsed.questions.length === 0) {
      throw new Error('Invalid questions array in response');
    }

    // Limit to requested count
    const questions = parsed.questions.slice(0, count);
    const topics = Array.isArray(parsed.topics) ? parsed.topics : [];

    return {
      questions,
      topics,
      generatedAt: new Date().toISOString(),
    };
  } catch (error) {
    // Handle JSON parse errors separately
    if (error instanceof SyntaxError) {
      console.error('[AI] Failed to parse suggested questions response:', error);
      // Return fallback generic questions
      return {
        questions: [
          'What services do you offer?',
          'How can I contact you?',
          'What are your hours?',
        ],
        topics: ['general'],
        generatedAt: new Date().toISOString(),
      };
    }

    handleOpenAIError(error, 'generateSuggestedQuestions');
    // handleOpenAIError throws, but TypeScript doesn't know this
    // This is unreachable but satisfies the return type
    throw error;
  }
}

/**
 * Validates that a question can be answered from content
 * Used to pre-validate suggested questions
 *
 * @param content - Bot content
 * @param question - Question to validate
 * @returns true if answerable
 */
export async function validateQuestionAnswerable(
  content: string,
  question: string
): Promise<boolean> {
  const client = getOpenAIClient();

  try {
    const response = await client.chat.completions.create({
      model: AI_MODELS.GPT4O_MINI,
      messages: [
        {
          role: 'system',
          content: `You are a validation assistant. Determine if the following question can be answered from the provided content.

CONTENT:
${content}

Respond with only "YES" if the question can be answered, or "NO" if it cannot.`,
        },
        { role: 'user', content: question },
      ],
      temperature: 0,
      max_tokens: 10,
    });

    const answer = response.choices[0]?.message?.content?.trim().toUpperCase();
    return answer === 'YES';
  } catch {
    // On error, assume answerable to avoid blocking
    return true;
  }
}
